
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import { LogIn, Activity, Mail, UserPlus, ArrowLeft } from 'lucide-react';
import {
    getAuth,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    OAuthProvider,
    updateProfile
} from 'firebase/auth';
import './LoginPage.css';

// Provider Icons as inline SVGs for consistency
const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

const MicrosoftIcon = () => (
    <svg width="20" height="20" viewBox="0 0 21 21">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
);

const AppleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
);

type AuthView = 'main' | 'email-signin' | 'email-signup';

const AdminLoginPage: React.FC = () => {
    const [view, setView] = useState<AuthView>('main');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const { login } = useAppContext();
    const navigate = useNavigate();

    // Detect if running on localhost (emulator mode)
    const isEmulatorMode = typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // --- OAuth Handlers ---

    const handleGoogleSignIn = async () => {
        setError('');
        setIsLoading(true);
        try {
            const auth = getAuth();
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            // Gatekeeper in AppContext handles the rest
            window.location.href = '/dashboard';
        } catch (err: any) {
            handleAuthError(err, 'Google');
        } finally {
            setIsLoading(false);
        }
    };

    const handleMicrosoftSignIn = async () => {
        setError('');
        setIsLoading(true);
        try {
            const auth = getAuth();
            const provider = new OAuthProvider('microsoft.com');
            provider.setCustomParameters({ prompt: 'select_account' });
            await signInWithPopup(auth, provider);
            window.location.href = '/dashboard';
        } catch (err: any) {
            handleAuthError(err, 'Microsoft');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAppleSignIn = async () => {
        setError('');
        setIsLoading(true);
        try {
            const auth = getAuth();
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');
            await signInWithPopup(auth, provider);
            window.location.href = '/dashboard';
        } catch (err: any) {
            handleAuthError(err, 'Apple');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Email/Password Handlers ---

    const handleEmailSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const auth = getAuth();
            await signInWithEmailAndPassword(auth, email, password);
            window.location.href = '/dashboard';
        } catch (err: any) {
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError('Invalid email or password.');
            } else {
                handleAuthError(err, 'Email');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        setIsLoading(true);
        try {
            const auth = getAuth();
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // Set display name if provided
            if (displayName && userCredential.user) {
                await updateProfile(userCredential.user, { displayName });
            }

            // The Gatekeeper in AppContext will create a 'pending' user profile
            // and redirect to the pending page
            window.location.href = '/dashboard';
        } catch (err: any) {
            if (err.code === 'auth/email-already-in-use') {
                setError('An account with this email already exists. Please sign in.');
            } else if (err.code === 'auth/weak-password') {
                setError('Password is too weak. Please use a stronger password.');
            } else {
                handleAuthError(err, 'Sign Up');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAuthError = (err: any, provider: string) => {
        console.error(`${provider} sign-in failed:`, err);
        if (err.code === 'auth/popup-closed-by-user') {
            setError('Sign-in was cancelled.');
        } else if (err.code === 'auth/popup-blocked') {
            setError('Popup was blocked. Please allow popups for this site.');
        } else if (err.code === 'auth/account-exists-with-different-credential') {
            setError('An account already exists with this email using a different sign-in method.');
        } else if (err.code === 'auth/operation-not-allowed') {
            setError(`${provider} sign-in is not enabled. Please contact the administrator.`);
        } else {
            setError(`Sign-in failed: ${err.message || 'Unknown error'}`);
        }
    };

    // --- Render ---

    const renderMainView = () => (
        <>
            <h2>Platform Access</h2>
            <p className="text-sm text-gray-500 mb-6">Sign in to access the AutoGain platform</p>

            {/* OAuth Providers */}
            <div className="space-y-3 mb-6">
                <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="auth-provider-button"
                >
                    <GoogleIcon />
                    <span>Continue with Google</span>
                </button>

                <button
                    type="button"
                    onClick={handleMicrosoftSignIn}
                    disabled={isLoading}
                    className="auth-provider-button"
                >
                    <MicrosoftIcon />
                    <span>Continue with Microsoft</span>
                </button>

                <button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={isLoading}
                    className="auth-provider-button"
                >
                    <AppleIcon />
                    <span>Continue with Apple</span>
                </button>
            </div>

            {/* Divider */}
            <div className="divider">
                <span>or</span>
            </div>

            {/* Email Options */}
            <div className="space-y-3">
                <button
                    type="button"
                    onClick={() => setView('email-signin')}
                    className="auth-provider-button email-button"
                >
                    <Mail size={20} />
                    <span>Sign in with Email</span>
                </button>

                <button
                    type="button"
                    onClick={() => setView('email-signup')}
                    className="auth-secondary-button"
                >
                    <UserPlus size={18} />
                    <span>Create Account</span>
                </button>
            </div>

            {error && <p className="login-error mt-4">{error}</p>}
        </>
    );

    const renderEmailSignIn = () => (
        <>
            <button onClick={() => setView('main')} className="back-button">
                <ArrowLeft size={16} /> Back
            </button>
            <h2>Sign In</h2>
            <form onSubmit={handleEmailSignIn} className="login-form">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="login-input"
                    placeholder="Email address"
                    required
                    autoFocus
                />
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-input"
                    placeholder="Password"
                    required
                />
                {error && <p className="login-error">{error}</p>}
                <button type="submit" className="login-button" disabled={isLoading}>
                    {isLoading ? 'Signing in...' : 'Sign In'} <LogIn size={18} className="inline-block ml-2" />
                </button>
            </form>
            <p className="text-center text-sm text-gray-500 mt-4">
                Don't have an account?{' '}
                <button onClick={() => setView('email-signup')} className="text-primary hover:underline">
                    Create one
                </button>
            </p>
        </>
    );

    const renderEmailSignUp = () => (
        <>
            <button onClick={() => setView('main')} className="back-button">
                <ArrowLeft size={16} /> Back
            </button>
            <h2>Create Account</h2>
            <p className="text-sm text-gray-500 mb-4">
                Your account will require administrator approval before access is granted.
            </p>
            <form onSubmit={handleEmailSignUp} className="login-form">
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="login-input"
                    placeholder="Full Name"
                    autoFocus
                />
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="login-input"
                    placeholder="Email address"
                    required
                />
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-input"
                    placeholder="Password (min 6 characters)"
                    required
                />
                <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="login-input"
                    placeholder="Confirm Password"
                    required
                />
                {error && <p className="login-error">{error}</p>}
                {successMessage && <p className="login-success">{successMessage}</p>}
                <button type="submit" className="login-button" disabled={isLoading}>
                    {isLoading ? 'Creating Account...' : 'Create Account'} <UserPlus size={18} className="inline-block ml-2" />
                </button>
            </form>
            <p className="text-center text-sm text-gray-500 mt-4">
                Already have an account?{' '}
                <button onClick={() => setView('email-signin')} className="text-primary hover:underline">
                    Sign in
                </button>
            </p>
        </>
    );

    return (
        <div className="full-screen-login">
            <div className="login-container">
                <div className="login-info">
                    <div className="flex items-center space-x-3 mb-8">
                        <div className="p-3 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 shadow-lg">
                            <Activity className="text-white" size={32} />
                        </div>
                        <span className="text-4xl font-extrabold text-white">AutoGain</span>
                    </div>
                    <h1 className="animate-on-scroll">Turn Your Engineering Data Into a Living System</h1>
                    <p className="animate-on-scroll">
                        Access the AutoGain platform to unify your data, streamline analysis, and drive proactive reliability decisions.
                    </p>
                </div>
                <div className="login-form-container">
                    {view === 'main' && renderMainView()}
                    {view === 'email-signin' && renderEmailSignIn()}
                    {view === 'email-signup' && renderEmailSignUp()}
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
